import { GridLayout, useContainerWidth } from 'react-grid-layout';
import type { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import type { Panel } from './types';

// ── Pure converters (API-independent; pinned by grid.test.ts) ───────────

export function panelsToLayout(panels: Panel[]): Layout {
  return panels.map((p) => ({ i: p.id, x: p.x, y: p.y, w: p.w, h: p.h }));
}

export function applyLayout(panels: Panel[], layout: Layout): Panel[] {
  const byId = new Map(layout.map((it) => [it.i, it]));
  return panels.map((p) => {
    const it = byId.get(p.id);
    return it ? { ...p, x: it.x, y: it.y, w: it.w, h: it.h } : p;
  });
}

// ── Component ───────────────────────────────────────────────────────────

const MOBILE_BREAKPOINT = 768;
const COLS = 12;
const ROW_HEIGHT = 40;

interface DashboardGridProps {
  panels: Panel[];
  editable?: boolean;
  onLayoutChange?: (panels: Panel[]) => void;
  renderPanel: (panel: Panel) => React.ReactNode;
}

export function DashboardGrid({
  panels,
  editable = false,
  onLayoutChange,
  renderPanel,
}: DashboardGridProps) {
  // useContainerWidth replaces the v1 WidthProvider HOC: it measures the
  // container via a ResizeObserver and hands back a ref + the live width.
  const { width, containerRef } = useContainerWidth();
  // RGL's types ship a React-19-shaped ref (RefObject<HTMLDivElement | null>),
  // but this app is on @types/react 18 where a <div ref> wants the non-null
  // RefObject<HTMLDivElement>. The runtime value is identical; only the type
  // skews, so we narrow it here.
  const ref = containerRef as React.RefObject<HTMLDivElement>;
  const isMobile = width > 0 && width < MOBILE_BREAKPOINT;

  // On narrow viewports we abandon the grid entirely and stack panels
  // vertically so they remain usable on phones.
  if (isMobile) {
    return (
      <div ref={ref} className="space-y-3">
        {panels.map((p) => (
          <div
            key={p.id}
            className="bg-surface-1 border border-surface-4 rounded-lg overflow-hidden"
          >
            {renderPanel(p)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div ref={ref}>
      <GridLayout
        width={width}
        layout={panelsToLayout(panels)}
        gridConfig={{ cols: COLS, rowHeight: ROW_HEIGHT }}
        dragConfig={{ enabled: editable, handle: '.drag-handle' }}
        resizeConfig={{ enabled: editable, handles: ['se'] }}
        onLayoutChange={(layout) => onLayoutChange?.(applyLayout(panels, layout))}
      >
        {panels.map((p) => (
          <div
            key={p.id}
            className="bg-surface-1 border border-surface-4 rounded-lg overflow-hidden"
          >
            {renderPanel(p)}
          </div>
        ))}
      </GridLayout>
    </div>
  );
}
